/* ══════════════════════════════════════════════
   THEME TOGGLE — Sun/Moon with HUD Glassmorphism
   ══════════════════════════════════════════════ */

/* ── Icon Variant (compact morph button) ── */
.iconBtn {
  all: unset;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: var(--bg-elevated);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition:
    background var(--duration-normal) var(--ease-default),
    box-shadow var(--duration-normal) var(--ease-default);
}

.iconBtn::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.2) 0%,
    rgba(255, 255, 255, 0.05) 50%,
    rgba(255, 255, 255, 0.1) 100%
  );
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  transition: background var(--duration-fast) var(--ease-default);
}

.iconBtn:hover {
  background: linear-gradient(135deg, rgba(240, 180, 41, 0.15) 0%, var(--bg-elevated) 100%);
  box-shadow: 0 0 20px rgba(240, 180, 41, 0.15);
}

.iconBtn:hover::before {
  background: linear-gradient(
    135deg,
    rgba(240, 180, 41, 0.5) 0%,
    rgba(240, 180, 41, 0.1) 50%,
    rgba(240, 180, 41, 0.3) 100%
  );
}

.iconOrb {
  position: relative;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Core - moon is silvery, sun is gold */
.core {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  /* Default: silvery moon */
  background: linear-gradient(135deg, #e8e8ec 0%, #c8c8d0 100%);
  box-shadow:
    0 0 6px rgba(255, 255, 255, 0.3),
    inset 0 0 4px rgba(255, 255, 255, 0.5);
  transition:
    background 0.4s var(--ease-default),
    box-shadow 0.4s var(--ease-default);
}

/* Moon crater - dark shadow that creates crescent */
.crater {
  position: absolute;
  width: 10px;
  height: 10px;
  background: var(--bg-base);
  border-radius: 50%;
  top: -2px;
  right: -3px;
  box-shadow: inset 1px 1px 2px rgba(0, 0, 0, 0.3);
  transition:
    transform 0.4s cubic-bezier(0.68, -0.2, 0.32, 1.3),
    opacity 0.4s var(--ease-default);
  transform: scale(1) translateX(0);
  opacity: 1;
}

/* Light mode: crater hides, core becomes gold sun */
.craterHidden {
  transform: scale(0) translateX(4px);
  opacity: 0;
}

/* Sun rays - brand gold */
.rays {
  position: absolute;
  inset: -5px;
  transition:
    opacity 0.4s var(--ease-default),
    transform 0.4s var(--ease-default);
  opacity: 0;
  transform: scale(0.5) rotate(-45deg);
}

.raysVisible {
  opacity: 1;
  transform: scale(1) rotate(0deg);
}

.ray {
  position: absolute;
  width: 2px;
  height: 2px;
  background: var(--accent-secondary);
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform-origin: center;
  transform:
    rotate(calc(var(--ray-index) * 45deg))
    translateY(-11px)
    translateX(-50%);
  box-shadow: 0 0 3px rgba(240, 180, 41, 0.8);
}

/* ── Switch Variant (full toggle) ── */
.switch {
  all: unset;
  width: 56px;
  height: 28px;
  border-radius: 20px;
  cursor: pointer;
  position: relative;
  display: block;
}

.track {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  /* Dark mode: deep space */
  background: linear-gradient(135deg, #0d1117 0%, #161b22 100%);
  overflow: hidden;
  transition: background 0.4s var(--ease-default);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.05);
}

/* Gradient border */
.track::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.03) 50%,
    rgba(255, 255, 255, 0.08) 100%
  );
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  transition: background 0.4s var(--ease-default);
}

/* Light mode: spectrum-full-9 cyan/sky */
.trackLight {
  background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.1),
    0 1px 0 rgba(255, 255, 255, 0.1);
}

.trackLight::before {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.4) 0%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.25) 100%
  );
}

/* Stars - gold tinted */
.stars {
  position: absolute;
  inset: 0;
  transition: opacity 0.4s var(--ease-default);
  opacity: 0;
}

.starsVisible {
  opacity: 1;
}

.star {
  position: absolute;
  width: var(--star-size, 2px);
  height: var(--star-size, 2px);
  background: rgba(240, 180, 41, 0.9);
  border-radius: 50%;
  left: var(--star-x);
  top: var(--star-y);
  box-shadow: 0 0 3px rgba(240, 180, 41, 0.8);
  animation: twinkle 2s ease-in-out infinite;
  animation-delay: calc(var(--star-x, 0) * 0.01s);
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(0.7);
  }
}

/* Cloud - positioned behind thumb in light mode */
.cloud {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%) translateX(-8px);
  width: 18px;
  height: 12px;
  transition:
    opacity 0.4s var(--ease-default),
    transform 0.4s var(--ease-default);
  opacity: 0;
}

.cloudVisible {
  opacity: 0.95;
  transform: translateY(-50%) translateX(0);
}

.cloudSvg {
  width: 100%;
  height: 100%;
  color: rgba(255, 255, 255, 0.95);
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

/* Thumb (sun/moon) */
.thumb {
  position: absolute;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  top: 3px;
  left: 3px;
  /* Moon: silvery */
  background: linear-gradient(135deg, #e8e8ec 0%, #d1d1d8 100%);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.4),
    inset 0 -2px 4px rgba(0, 0, 0, 0.1),
    inset 0 2px 4px rgba(255, 255, 255, 0.9);
  transition:
    transform 0.4s cubic-bezier(0.68, -0.2, 0.32, 1.3),
    background 0.4s var(--ease-default),
    box-shadow 0.4s var(--ease-default);
}

/* Sun: brand gold */
.thumbLight {
  transform: translateX(28px);
  background: linear-gradient(135deg, var(--accent-secondary) 0%, #d99e1e 100%);
  box-shadow:
    0 2px 8px rgba(240, 180, 41, 0.5),
    0 0 16px rgba(240, 180, 41, 0.4),
    inset 0 -2px 4px rgba(0, 0, 0, 0.15),
    inset 0 2px 4px rgba(255, 255, 255, 0.4);
}

/* Moon crescent shadow */
.moonShadow {
  position: absolute;
  width: 16px;
  height: 16px;
  background: linear-gradient(135deg, #0d1117 0%, #161b22 100%);
  border-radius: 50%;
  top: -2px;
  right: -4px;
  transition:
    opacity 0.4s var(--ease-default),
    transform 0.4s cubic-bezier(0.68, -0.2, 0.32, 1.3);
  transform: scale(1) translate(0, 0);
  opacity: 1;
}

/* Sun state - crescent hides */
.moonShadowHidden {
  transform: scale(0.5) translate(8px, -4px);
  opacity: 0;
}

/* ── Light mode adjustments ── */
:global([data-theme='light']) .iconBtn {
  background: rgba(255, 255, 255, 0.8);
}

:global([data-theme='light']) .iconBtn::before {
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.02) 50%,
    rgba(0, 0, 0, 0.05) 100%
  );
}

:global([data-theme='light']) .iconBtn:hover {
  background: linear-gradient(135deg, rgba(240, 180, 41, 0.2) 0%, rgba(255, 255, 255, 0.8) 100%);
}

/* Light mode: icon core becomes gold sun */
:global([data-theme='light']) .core {
  background: linear-gradient(135deg, var(--accent-secondary) 0%, #d99e1e 100%);
  box-shadow:
    0 0 10px rgba(240, 180, 41, 0.6),
    inset 0 0 5px rgba(255, 255, 255, 0.4);
}

:global([data-theme='light']) .crater {
  background: var(--bg-base);
}

:global([data-theme='light']) .switch {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
